import { GAME_CONFIG } from '@config/game.config';
import { getGameRegistryEntry } from '@config/game-registry';
import { getGameFromUrl } from '@core/utils/urlParser';
import { resolveGameRouteSlug } from '@core/utils/resolveGameRouteSlug';

/**
 * Public asset paths live under `/games/<folder>/…` while route slugs may use
 * hyphens (e.g. `sky-sniffer`). Registry `audioSlug` is the folder name.
 */
export function resolveAssetFolderSlug(
  gameSlug: string | undefined | null
): string {
  if (!gameSlug) {
    return '';
  }

  // Resolve branding aliases before lowercasing so "SkyX" → sky-x → skyx_new,
  // not legacy "skyx" (which would nest assets as /games/skyx_new/SkyX/…).
  const resolved = resolveGameRouteSlug(gameSlug);
  const entry =
    getGameRegistryEntry(resolved) ?? getGameRegistryEntry(gameSlug);
  if (entry?.audioSlug) {
    return entry.audioSlug;
  }

  // Case-insensitive fallback for other slugs (only when no alias rewrite applied).
  if (resolved === gameSlug) {
    const lower = gameSlug.toLowerCase();
    if (lower !== gameSlug) {
      const lowerEntry = getGameRegistryEntry(lower);
      if (lowerEntry?.audioSlug) {
        return lowerEntry.audioSlug;
      }
    }
  }

  const routeSlug = getGameFromUrl();
  if (routeSlug && routeSlug !== gameSlug) {
    const routeEntry = getGameRegistryEntry(routeSlug);
    if (routeEntry?.audioSlug) {
      return routeEntry.audioSlug;
    }
  }

  return resolved.toLowerCase();
}

/** Vite deploy base without trailing slash (e.g. `/ps/crash`, or empty at site root). */
export function getAppBasePath(): string {
  const base = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
  if (!base || base === '/') {
    return '';
  }
  return base.startsWith('/') ? base : `/${base}`;
}

/**
 * Prefix an absolute public path with the Vite base (e.g. `/games/...` → `/ps/crash/games/...`).
 */
export function resolvePublicUrl(path: string): string {
  if (!path) {
    return path;
  }
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(path)) {
    return path;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const base = getAppBasePath();
  if (!base) {
    return normalized;
  }
  return `${base}${normalized}`;
}

/** Origin + optional Vite `base` for static files copied from `public/`. */
export function getStaticAssetOrigin(): string {
  const origin =
    (typeof window !== 'undefined' && GAME_CONFIG.cdnBase) ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const basePath = getAppBasePath();
  if (!basePath) {
    return origin;
  }
  return `${origin}${basePath}`;
}

function encodeAssetPath(relativePath: string): string {
  return relativePath.split('/').map(encodeURIComponent).join('/');
}

/**
 * Build a URL for a file under `public/games/`.
 * Manifest entries that already start with `games/` are resolved from site root.
 */
export function buildGamePublicAssetUrl(
  assetFolderSlug: string,
  relativeAssetPath: string,
  baseUrl?: string
): string {
  if (!relativeAssetPath) {
    return '';
  }

  // Pass through already-resolved image URLs (e.g. CDN/data/blob player avatars).
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(relativeAssetPath)) {
    return relativeAssetPath;
  }

  const origin = baseUrl || getStaticAssetOrigin();
  const encodedPath = encodeAssetPath(relativeAssetPath);

  if (relativeAssetPath.startsWith('games/')) {
    return `${origin}/${encodedPath}`;
  }

  if (!assetFolderSlug) {
    return '';
  }

  return `${origin}/games/${assetFolderSlug}/${encodedPath}`;
}
